import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, 
  Radio,
  ChevronUp,
  ChevronDown,
  X,
  Globe,
  ExternalLink
} from 'lucide-react';

// Rwandan Radio Stations with embed URLs
const rwandanStations = [
  {
    id: 'radio-rwanda',
    name: 'Radio Rwanda',
    genre: 'National Radio',
    country: '🇷🇼 Rwanda',
    embedUrl: 'https://onlineradiobox.com/rw/rwanda/?embed=1',
    radioGarden: 'https://radio.garden/listen/radio-rwanda/mRwLKaWe'
  },
  {
    id: 'kt-radio',
    name: 'KT Radio 96.7',
    genre: 'News & Music',
    country: '🇷🇼 Rwanda',
    embedUrl: 'https://onlineradiobox.com/rw/kt/?embed=1',
    radioGarden: 'https://radio.garden/listen/kt-radio/JfvKxQ5p'
  },
  {
    id: 'flash-fm-rwanda',
    name: 'Flash FM Rwanda',
    genre: 'Hits & Entertainment',
    country: '🇷🇼 Rwanda',
    embedUrl: 'https://onlineradiobox.com/rw/flashfm/?embed=1',
    radioGarden: null
  },
  {
    id: 'kiss-fm-rwanda',
    name: 'Kiss FM Rwanda',
    genre: 'Pop & Hits',
    country: '🇷🇼 Rwanda',
    embedUrl: 'https://onlineradiobox.com/rw/kissfm/?embed=1',
    radioGarden: null
  },
  {
    id: 'isango-star',
    name: 'Isango Star',
    genre: 'Entertainment',
    country: '🇷🇼 Rwanda',
    embedUrl: 'https://onlineradiobox.com/rw/isangostar/?embed=1',
    radioGarden: null
  },
];

// Amapiano & South African Stations
const amapianoStations = [
  {
    id: 'metro-fm',
    name: 'Metro FM',
    genre: 'Urban & Amapiano',
    country: '🇿🇦 South Africa',
    embedUrl: 'https://onlineradiobox.com/za/metrofm/?embed=1',
    radioGarden: 'https://radio.garden/listen/metro-fm/sPm8rBwb'
  },
  {
    id: 'yfm',
    name: 'YFM 99.2',
    genre: 'Youth & Amapiano',
    country: '🇿🇦 South Africa',
    embedUrl: 'https://onlineradiobox.com/za/yfm/?embed=1',
    radioGarden: 'https://radio.garden/listen/y-fm/e2N0v9Kp'
  },
  {
    id: 'ukhozi-fm',
    name: 'Ukhozi FM',
    genre: 'Zulu Music & Amapiano',
    country: '🇿🇦 South Africa',
    embedUrl: 'https://onlineradiobox.com/za/ukhozifm/?embed=1',
    radioGarden: 'https://radio.garden/listen/ukhozi-fm/8HvMJNpb'
  },
  {
    id: '5fm',
    name: '5FM',
    genre: 'Contemporary Hits',
    country: '🇿🇦 South Africa',
    embedUrl: 'https://onlineradiobox.com/za/5fm/?embed=1',
    radioGarden: 'https://radio.garden/listen/5fm/mCRMsZhb'
  },
  {
    id: 'kaya-fm',
    name: 'Kaya FM 959',
    genre: 'Urban Adult',
    country: '🇿🇦 South Africa',
    embedUrl: 'https://onlineradiobox.com/za/kayafm/?embed=1',
    radioGarden: 'https://radio.garden/listen/kaya-fm/XjWMgkQD'
  },
];

const allStations = [...rwandanStations, ...amapianoStations];

export default function AfricanMusicPlayer() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [currentStation, setCurrentStation] = useState(allStations[0]);
  const [activeTab, setActiveTab] = useState('rwanda'); // 'rwanda' or 'amapiano'
  const [useRadioGarden, setUseRadioGarden] = useState(false);

  const displayedStations = activeTab === 'rwanda' ? rwandanStations : amapianoStations;

  const selectStation = (station) => {
    setCurrentStation(station);
  };

  // Get the embed URL based on selected source
  const getEmbedUrl = () => {
    if (useRadioGarden && currentStation.radioGarden) {
      // Radio Garden embed format
      const gardenId = currentStation.radioGarden.split('/').pop();
      return `https://radio.garden/embed/listen/${gardenId}`;
    }
    return currentStation.embedUrl;
  };

  // Minimized floating button
  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full shadow-lg flex items-center justify-center"
        data-testid="show-music-player"
      >
        <Music className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500 border-2 border-white"></span>
        </span>
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-6 right-6 z-50"
      data-testid="african-music-player"
    >
      <div className={`bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl shadow-2xl overflow-hidden border border-stone-700 ${isExpanded ? 'w-96' : 'w-80'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-white" />
              <span className="font-bold text-white text-sm">African Radio</span>
              <span className="text-white/70 text-xs">LIVE</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="expand-music-player"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-white" />
                ) : (
                  <ChevronUp className="w-4 h-4 text-white" />
                )}
              </button>
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="close-music-player"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Country Tabs */}
        <div className="flex border-b border-stone-700">
          <button
            onClick={() => setActiveTab('rwanda')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'rwanda' 
                ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-500' 
                : 'text-stone-400 hover:text-white'
            }`}
          >
            🇷🇼 Rwanda
          </button>
          <button
            onClick={() => setActiveTab('amapiano')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'amapiano' 
                ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-500' 
                : 'text-stone-400 hover:text-white'
            }`}
          >
            🇿🇦 Amapiano
          </button>
        </div>

        {/* Current Station Info */}
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Music className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {currentStation.name}
              </p>
              <p className="text-stone-400 text-xs truncate">
                {currentStation.genre} • {currentStation.country}
              </p>
            </div>
            {currentStation.radioGarden && (
              <a
                href={currentStation.radioGarden}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-stone-700 rounded-lg transition-colors"
                title="Open in Radio Garden"
              >
                <ExternalLink className="w-4 h-4 text-stone-400" />
              </a>
            )}
          </div>

          {/* Embedded Player */}
          <div className="rounded-xl overflow-hidden bg-stone-950 mb-4">
            <iframe
              src={getEmbedUrl()}
              width="100%"
              height="120"
              frameBorder="0"
              allow="autoplay"
              className="w-full"
              title={`${currentStation.name} Radio Player`}
            />
          </div>

          {/* Source Toggle */}
          {currentStation.radioGarden && (
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-stone-500 text-xs">Source:</span>
              <button
                onClick={() => setUseRadioGarden(false)}
                className={`px-2 py-1 text-xs rounded ${!useRadioGarden ? 'bg-amber-500 text-white' : 'bg-stone-700 text-stone-400'}`}
              >
                OnlineRadioBox
              </button>
              <button
                onClick={() => setUseRadioGarden(true)}
                className={`px-2 py-1 text-xs rounded ${useRadioGarden ? 'bg-amber-500 text-white' : 'bg-stone-700 text-stone-400'}`}
              >
                Radio Garden
              </button>
            </div>
          )}

          {/* Station List (Expanded) */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="border-t border-stone-700 pt-3">
                  <p className="text-stone-400 text-xs mb-2 flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {activeTab === 'rwanda' ? 'Rwandan Stations' : 'South African Stations'}
                  </p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {displayedStations.map((station) => (
                      <button
                        key={station.id}
                        onClick={() => selectStation(station)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          currentStation.id === station.id
                            ? 'bg-amber-500/20 border border-amber-500/30'
                            : 'hover:bg-stone-700/50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          currentStation.id === station.id
                            ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                            : 'bg-stone-700'
                        }`}>
                          <Radio className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            currentStation.id === station.id ? 'text-amber-400' : 'text-white'
                          }`}>
                            {station.name}
                          </p>
                          <p className="text-stone-500 text-xs truncate">{station.genre}</p>
                        </div>
                        {currentStation.id === station.id && (
                          <div className="flex gap-0.5">
                            <span className="w-1 h-3 bg-amber-500 rounded-full animate-pulse" />
                            <span className="w-1 h-4 bg-amber-500 rounded-full animate-pulse" style={{ animationDelay: '75ms' }} />
                            <span className="w-1 h-2 bg-amber-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-stone-900/50 border-t border-stone-700">
          <p className="text-stone-500 text-xs text-center">
            🎵 Live African Radio Streaming
          </p>
        </div>
      </div>
    </motion.div>
  );
}
